<template>
  <div class="bg-white rounded-2xl p-7 border border-[#1c4848]/10 shadow-sm flex flex-col">
    <div class="mb-6">
      <h3 class="text-[20px] font-medium tracking-tight text-[#1c4848]">{{ title }}</h3>
      <p class="text-[15px] text-[#1c4848]/55 mt-1">{{ subtitle }}</p>
    </div>

    <p v-if="!rows.length" class="text-base text-[#1c4848]/40 py-8 text-center">{{ emptyText }}</p>

    <ol v-else class="space-y-5">
      <li v-for="row in visibleRows" :key="row.key">
        <div class="flex items-baseline justify-between gap-3">
          <span class="text-[16px] font-normal text-[#1c4848] truncate" :title="row.key">{{ row.label }}</span>
          <span class="text-[17px] font-medium text-[#1c4848] shrink-0 tabular-nums">
            {{ row.display }}
            <span v-if="row.hint" class="text-[13px] font-normal text-[#1c4848]/45 ml-1.5">{{ row.hint }}</span>
          </span>
        </div>
        <div class="w-full h-1.5 rounded-full bg-[#1c4848]/[0.06] overflow-hidden mt-2">
          <div class="h-full rounded-full transition-all duration-500" :style="{ width: `${Math.max(share(row), 2)}%`, backgroundColor: row.color || color }" />
        </div>
      </li>
    </ol>

    <button
      v-if="rows.length > limit"
      class="mt-5 self-start text-[15px] font-medium text-[#1c4848]/60 hover:text-[#1c4848] transition-colors"
      @click="expanded = !expanded"
    >
      {{ expanded ? 'Show less' : `Show all ${rows.length}` }}
    </button>

    <p v-if="note" class="text-[13px] text-[#1c4848]/45 mt-5 leading-relaxed">{{ note }}</p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

/**
 * A ranked list where the bar is not a count: seconds, a percentage, a share.
 * Each row brings its own text, so the card never has to guess a unit.
 */
const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  /** [{ key, label, value, display, hint?, color? }] — `value` sizes the bar. */
  rows: { type: Array, default: () => [] },
  /** Bars are relative to the largest value unless a fixed maximum (e.g. 100 %) is given. */
  max: { type: Number, default: 0 },
  color: { type: String, default: '#1c4848' },
  limit: { type: Number, default: 8 },
  emptyText: { type: String, default: 'Nothing recorded in this period yet.' },
  note: { type: String, default: '' },
});

const expanded = ref(false);
const visibleRows = computed(() => (expanded.value ? props.rows : props.rows.slice(0, props.limit)));
const top = computed(() => props.max || props.rows.reduce((m, row) => Math.max(m, Number(row.value) || 0), 0));

function share(row) {
  return top.value ? ((Number(row.value) || 0) / top.value) * 100 : 0;
}
</script>
